import { apiClient } from '@/services/api/client'
import { useAuthStore } from '@/stores/authStore'

// Pre-login fallback and the legacy shared key. lib/identityStorage.ts sweeps it on every
// identity change, so it can never carry progress from one login to the next.
const LEGACY_TOUR_PROGRESS_KEY = 'vharta_tour_progress'

// Progress for a signed-in user lives under their own key. It is deliberately NOT a prefix
// of any key identityStorage sweeps: the sweep ran on every login and erased the progress,
// so the onboarding tour came back each time. Keyed by user id, a second person on the same
// browser still starts with their own empty record.
const USER_TOUR_PROGRESS_KEY_PREFIX = 'vharta.tour.progress.'

function progressKey(): string {
  const userId = useAuthStore.getState().user?.id
  return userId ? USER_TOUR_PROGRESS_KEY_PREFIX + userId : LEGACY_TOUR_PROGRESS_KEY
}

export interface TourProgressRecord {
  [tourId: string]: {
    completed: boolean
    completedAt?: string
    startedAt?: string
    lastStep?: number
  }
}

export const tourProgressService = {
  load(): TourProgressRecord {
    try {
      const raw = localStorage.getItem(progressKey())
      if (!raw) return {}
      const parsed: unknown = JSON.parse(raw)
      // Guard against a corrupted / legacy value that is valid JSON but not a
      // plain object (e.g. the string "true" → boolean true). Without this,
      // callers like markStarted do `progress[tourId] = {...}` on a primitive
      // and throw "Cannot create property '<tour>' on boolean 'true'".
      if (
        parsed === null ||
        typeof parsed !== 'object' ||
        Array.isArray(parsed)
      ) {
        // Self-healing eviction: intentionally drop the bad value here (a write
        // from within a read) so a corrupt primitive cannot keep poisoning
        // every future read until the user manually clears storage.
        try {
          localStorage.removeItem(progressKey())
        } catch {
          // ignore storage errors
        }
        return {}
      }
      return parsed as TourProgressRecord
    } catch {
      return {}
    }
  },

  save(progress: TourProgressRecord): void {
    try {
      localStorage.setItem(progressKey(), JSON.stringify(progress))
    } catch {
      // Storage quota exceeded or unavailable — silently ignore
    }
  },

  markStarted(tourId: string): TourProgressRecord {
    const progress = tourProgressService.load()
    progress[tourId] = {
      ...progress[tourId],
      startedAt: new Date().toISOString(),
      completed: progress[tourId]?.completed ?? false,
    }
    tourProgressService.save(progress)
    return progress
  },

  markCompleted(tourId: string): TourProgressRecord {
    const progress = tourProgressService.load()
    progress[tourId] = {
      ...progress[tourId],
      completed: true,
      completedAt: new Date().toISOString(),
    }
    tourProgressService.save(progress)
    return progress
  },

  updateLastStep(tourId: string, stepIndex: number): void {
    const progress = tourProgressService.load()
    if (progress[tourId]) {
      progress[tourId].lastStep = stepIndex
      tourProgressService.save(progress)
    }
  },

  reset(): TourProgressRecord {
    const empty: TourProgressRecord = {}
    tourProgressService.save(empty)
    return empty
  },
}

// actionToEventType maps the UI action vocabulary to the canonical
// tour_analytics_events.event_type values the backend (and its DB CHECK) accept.
// Keep in sync with internal/domain/tour TourEventType constants.
// actionToEventType maps the UI action vocabulary to the canonical
// tour_analytics_events.event_type values the backend (and its DB CHECK) accept.
// Complete vs internal/domain/tour TourEventType constants (incl. abandoned).
const actionToEventType: Record<TourAction, string> = {
  started: 'tour_started',
  completed: 'tour_completed',
  skipped: 'tour_skipped',
  abandoned: 'tour_abandoned',
  step_viewed: 'tour_step_viewed',
  ai_hint_requested: 'ai_hint_requested',
}

type TourAction =
  | 'started'
  | 'completed'
  | 'skipped'
  | 'abandoned'
  | 'step_viewed'
  | 'ai_hint_requested'

export const tourAnalyticsService = {
  track(event: {
    tourId: string
    action: TourAction
    stepIndex?: number
    stepTitle?: string
  }): void {
    const eventType = actionToEventType[event.action]
    if (!eventType) return
    // Conform to the wired backend contract: POST /tenant/tours/events with a
    // batched { events: [...] } body using snake_case AnalyticsEvent fields.
    // (Previously POSTed to a non-existent /tenant/analytics/tour-events → 404.)
    apiClient
      .post('/tenant/tours/events', {
        events: [
          {
            event_type: eventType,
            tour_id: event.tourId,
            // omit step_index entirely for non-step events rather than send null
            ...(event.stepIndex !== undefined && { step_index: event.stepIndex }),
            ai_hint_requested: event.action === 'ai_hint_requested',
            timestamp: new Date().toISOString(),
          },
        ],
      })
      .catch(() => {})
  },
}
